import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { IArticleSpecialDeliveryDateInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { logToConsole } from '~/logging/consoleLogger';
import { useBatchFetch } from '~/shell/hooks/useBatchFetch';
import { useLoginInfo } from '~/shell/hooks/useLoginInfo';
import { clientFetch } from '~/common/utils/clientFetch.client';

async function fetchArticleSpecialDeliveryDates(
  ids: number[]
): Promise<Record<number, IArticleSpecialDeliveryDateInfoDtoData | null>> {
  const mainArticleIds = ids.join(',');

  try {
    const response = await clientFetch(`/infra/resources/articleSpecialDeliveryDates?mainArticleIds=${mainArticleIds}`);
    const dates = (await response.json()) as IArticleSpecialDeliveryDateInfoDtoData[];

    const result: Record<number, IArticleSpecialDeliveryDateInfoDtoData | null> = {};
    dates.forEach(item => {
      result[item.mainArticleId] = item;
    });

    return result;
  } catch (e) {
    logToConsole({
      loggerName: 'special-delivery-date-context',
      logLevel: 'ERROR',
      message: 'Error fetching special delivery dates',
      args: [e]
    });
    throw e;
  }
}

interface SpecialDeliveryDateContextValue {
  fetchDeliveryDate: (mainArticleIds: number[]) => Promise<IArticleSpecialDeliveryDateInfoDtoData[] | null>;
}

const SpecialDeliveryDateContext = createContext<SpecialDeliveryDateContextValue | null>(null);

interface SpecialDeliveryDateProviderProps {
  children: ReactNode;
  flushTimeout: number;
  flushQueueSize: number;
  maxCacheItems: number;
}

export const SpecialDeliveryDateProvider = ({
  children,
  flushTimeout,
  flushQueueSize,
  maxCacheItems
}: SpecialDeliveryDateProviderProps) => {
  const { fetch: fetchDeliveryDate } = useBatchFetch<IArticleSpecialDeliveryDateInfoDtoData>({
    fetchFn: fetchArticleSpecialDeliveryDates,
    flushTimeout,
    flushQueueSize,
    maxCacheItems
  });

  const value: SpecialDeliveryDateContextValue = useMemo(
    () => ({
      fetchDeliveryDate
    }),
    [fetchDeliveryDate]
  );

  return <SpecialDeliveryDateContext.Provider value={value}>{children}</SpecialDeliveryDateContext.Provider>;
};

export function useSpecialDeliveryDateContext() {
  const context = useContext(SpecialDeliveryDateContext);
  if (!context) {
    throw new Error('useSpecialDeliveryDateContext must be used within a SpecialDeliveryDateProvider');
  }
  return context;
}

export function useSpecialDeliveryDate(mainArticleId: number): Date | undefined {
  const { fetchDeliveryDate } = useSpecialDeliveryDateContext();
  const { loginInfo } = useLoginInfo();
  const [clientDate, setClientDate] = useState<Date | undefined>(undefined);
  const hasFetched = useRef(false);

  const doFetch = useCallback(async () => {
    const result = await fetchDeliveryDate([mainArticleId]);
    const match = result?.find(x => x.mainArticleId === mainArticleId && x.specialDeliveryDateInfo);
    if (match) {
      setClientDate(new Date(match.specialDeliveryDateInfo));
    }
  }, [mainArticleId, fetchDeliveryDate]);

  useEffect(() => {
    if (loginInfo && !hasFetched.current) {
      hasFetched.current = true;
      doFetch();
    }
  }, [loginInfo, doFetch]);

  return clientDate;
}
